import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import s from './EditInventoryModal.module.css';
import { TitleContainer } from './Title/Title';
import { Loader } from '../Loader/Loader';
import { MainImage } from './MainImage/MainImage';
import { SettingsBlock } from './Settings/SettingsBlock';
import { Status } from './Status/Status';
import { Dropzone } from './Dropzone/Dropzone';
import { HistoryLogsBlock } from './HistoryLogsBlock/HistoryLogsBlock';
import { Description } from './Description/Description';
import { setIsCopyMode, setOpenedInventoryGuid } from '../../store/slices/inventorySlice';
import { useIsMounted } from '../UseIsMounted';
import { setIsShowToast } from '../../store/slices/toastSlice';

export function EditInventoryModal({ inventoryGuid, warehouseId, deleteInventory, setImage }) {
    const [inventoryData, setInventoryData] = useState(null);
    const isMounted = useIsMounted();
    const isCopyMode = useSelector((state) => state.inventory.isCopyMode);
    const dispatch = useDispatch();

    useEffect(() => {
        const url = helpable.replaceUrlParameters(routable.getRoute('viewInventory'), {
            _guid_: inventoryGuid,
        });
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
            },
        })
            .then((response) => response.json())
            .then((data) => {
                if (isMounted) {
                    setInventoryData(data.response);
                }

                if (isCopyMode) {
                    dispatch(setIsShowToast(true));
                }
            });
    }, []);

    const board = useSelector((state) => state.board);
    const warehouseTitle = board.find((warehouse) => warehouse.id === warehouseId).title;
    const warehouse = board.find((item) => item.id === warehouseId);
    const inventory = warehouse.items.find((item) => item.guid === inventoryGuid);
    const freeDate = inventory.free_date;
    const hasFullAccess = useSelector((state) => state.restrictions);
    const historyLogsBlockRef = useRef(null);

    const setNewInventoryLog = useCallback(
        (log) => historyLogsBlockRef.current && historyLogsBlockRef.current.addLog(log),
        [historyLogsBlockRef]
    );
    return (
        <>
            <div className={s.inventory}>
                {inventoryData && (
                    <div>
                        <div className={s.inventoryInfoBlock}>
                            <MainImage
                                setImage={setImage}
                                warehouseId={warehouseId}
                                setInventoryData={setInventoryData}
                                inventoryData={inventoryData}
                                setNewInventoryLog={setNewInventoryLog}
                                inventoryGuid={inventoryGuid}
                                previewUrl={inventoryData.preview_url}
                            />
                            <div className={s.inventorySettings}>
                                <TitleContainer
                                    titleText={inventoryData.title}
                                    inventoryGuid={inventoryGuid}
                                    warehouseId={warehouseId}
                                    hasFullAccess={hasFullAccess}
                                />
                                <SettingsBlock
                                    amount={inventoryData.amount}
                                    unit={inventoryData.unit}
                                    price={inventoryData.price}
                                    cost={inventoryData.cost}
                                    inventoryNumber={inventoryData.inventory_number}
                                    purchaseDate={inventoryData.purchase_date}
                                    inventoryGuid={inventoryGuid}
                                    warehouseId={warehouseId}
                                    setNewInventoryLog={setNewInventoryLog}
                                />
                            </div>
                        </div>
                        <div className={s.location}>
                            <h4 className={s.subTitle}>
                                {i18next.t('EditInventoryModal.return.location')}
                            </h4>
                            <span className={s.locationDescription}>{warehouseTitle}</span>
                        </div>
                        <Status
                            warehouseId={warehouseId}
                            inventoryGuid={inventoryGuid}
                            status={inventoryData.status}
                            freeDate={freeDate}
                            setNewInventoryLog={setNewInventoryLog}
                            setInventoryData={setInventoryData}
                            inventoryData={inventoryData}
                            hasFullAccess={hasFullAccess}
                            deleteInventory={deleteInventory}
                        />

                        <Description
                            description={inventoryData.description}
                            inventoryGuid={inventoryGuid}
                        />
                        <div className={s.dropzone}>
                            <Dropzone
                                setImage={setImage}
                                warehouseId={warehouseId}
                                setNewInventoryLog={setNewInventoryLog}
                                inventoryGuid={inventoryGuid}
                                inventoryData={inventoryData}
                                setInventoryData={setInventoryData}
                            />
                        </div>
                        <div className={s.historyBlockWrapper}>
                            <h4 className={cx(s.subTitle, s.historyTitle)}>
                                {i18next.t('EditInventoryModal.return.historyBlockWrapper')}
                            </h4>
                            <HistoryLogsBlock ref={historyLogsBlockRef} guid={inventoryGuid} />
                        </div>
                    </div>
                )}
                <div
                    className={s.closeBtn}
                    onClick={() => {
                        dispatch(setOpenedInventoryGuid(null));
                        dispatch(setIsCopyMode(false));
                    }}
                />

                {!inventoryData && (
                    <Loader size="mediumDot" color="grayDot" loaderWidth="loaderWidth40" />
                )}
            </div>
        </>
    );
}
